/**
 * Plant Mapping Store
 * Manages plant mapping state using Zustand
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { 
  PlantLayout, 
  WorkshopPosition, 
  PlantZone, 
  WorkshopBasic,
  PlantMappingDashboard,
  DragPosition,
  plantLayoutApi,
  workshopPositionApi,
  plantZoneApi,
  plantMappingUtils
} from '../services/plantMapping';

interface PlantMappingState {
  // Data
  layout: PlantLayout | null;
  positions: WorkshopPosition[];
  zones: PlantZone[];
  unpositionedWorkshops: WorkshopBasic[];
  statistics: PlantMappingDashboard['statistics'] | null;
  
  // UI State
  loading: boolean;
  error: string | null;
  selectedWorkshop: WorkshopPosition | null;
  selectedZone: PlantZone | null;
  isDragging: boolean;
  draggedWorkshop: WorkshopPosition | null;
  
  // View State
  zoom: number;
  centerX: number;
  centerY: number;
  showZones: boolean;
  showUnpositioned: boolean;
  filterStatus: string;
  filterCategory: string;
  filterDepartment: number | null;
  filterDeadlineFrom: string | null;
  filterDeadlineTo: string | null;
  filterAlvId: string | null;
  
  // Actions
  fetchDashboard: (layoutId: number) => Promise<void>;
  fetchDashboardByPlant: (plantId: number) => Promise<'loaded' | 'no_layout' | 'error'>;
  clearMappingData: () => void;
  setLayoutBackgroundOpacity: (opacity: number) => void;
  updatePosition: (id: number, x: number, y: number, zone?: string) => Promise<void>;
  bulkUpdatePositions: (positions: DragPosition[]) => Promise<void>;
  autoPositionWorkshops: (strategy: string, options?: any) => Promise<void>;
  createMissingPositions: () => Promise<void>;
  
  // Zone Actions
  createZone: (zone: Partial<PlantZone>) => Promise<void>;
  updateZone: (id: number, data: Partial<PlantZone>) => Promise<void>;
  deleteZone: (id: number) => Promise<void>;
  
  // UI Actions
  setSelectedWorkshop: (workshop: WorkshopPosition | null) => void;
  setSelectedZone: (zone: PlantZone | null) => void;
  setDragging: (isDragging: boolean, workshop?: WorkshopPosition | null) => void;
  setZoom: (zoom: number) => void;
  setCenter: (x: number, y: number) => void;
  setShowZones: (show: boolean) => void;
  setShowUnpositioned: (show: boolean) => void;
  setFilters: (filters: {
    status?: string;
    category?: string;
    department?: number | null;
    deadlineFrom?: string | null;
    deadlineTo?: string | null;
    alvId?: string | null;
  }) => void;
  
  // Utility Actions
  getFilteredPositions: () => WorkshopPosition[];
  getFilteredUnpositionedWorkshops: () => WorkshopBasic[];
  getWorkshopAtPosition: (x: number, y: number, threshold?: number) => WorkshopPosition | null;
  getZoneAtPosition: (x: number, y: number) => PlantZone | null;
  clearError: () => void;
  reset: () => void;
}

const STORAGE_KEY_FROM = 'kata_deadline_from';
const STORAGE_KEY_TO = 'kata_deadline_to';
const EVENT_NAME = 'kata_date_filter_change';

export const usePlantMappingStore = create<PlantMappingState>()(
  devtools(
    (set, get) => {
      // Listen for custom events
      if (typeof window !== 'undefined') {
        window.addEventListener(EVENT_NAME, (e: any) => {
          const { startDate, endDate } = e.detail;
          const { filterDeadlineFrom, filterDeadlineTo } = get();
          
          // Use loose equality to handle null vs undefined vs empty string
          const startChanged = (startDate || null) !== (filterDeadlineFrom || null);
          const endChanged = (endDate || null) !== (filterDeadlineTo || null);

          if (startChanged || endChanged) {
             set({ 
               filterDeadlineFrom: startDate || null, 
               filterDeadlineTo: endDate || null 
             });
          }
        });
      }

      return {
      // Initial state
      layout: null,
      positions: [],
      zones: [],
      unpositionedWorkshops: [],
      statistics: null,
      
      loading: false,
      error: null,
      selectedWorkshop: null,
      selectedZone: null,
      isDragging: false,
      draggedWorkshop: null,
      
      zoom: 1,
      centerX: 0.5,
      centerY: 0.5,
      showZones: true,
      showUnpositioned: true,
      filterStatus: '',
      filterCategory: '',
      filterDepartment: null,
      filterDeadlineFrom: localStorage.getItem(STORAGE_KEY_FROM) || null,
      filterDeadlineTo: localStorage.getItem(STORAGE_KEY_TO) || null,
      filterAlvId: null,

      // Dashboard Actions
      fetchDashboard: async (layoutId: number) => {
        set({ loading: true, error: null });
        try {
          const response = await plantLayoutApi.getDashboard(layoutId);
          const data = response.data;
          
          set({
            layout: data.layout,
            positions: data.positions,
            zones: data.zones,
            unpositionedWorkshops: data.unpositioned_workshops,
            statistics: data.statistics,
            zoom: data.layout.default_zoom,
            centerX: data.layout.default_center_x,
            centerY: data.layout.default_center_y,
            loading: false
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to fetch plant mapping dashboard',
            loading: false 
          });
        }
      },

      fetchDashboardByPlant: async (plantId: number) => {
        set({ loading: true, error: null });
        try {
          const response = await plantLayoutApi.getDashboardByPlant(plantId);
          const data = response.data;
          
          set({
            layout: data.layout,
            positions: data.positions,
            zones: data.zones,
            unpositionedWorkshops: data.unpositioned_workshops,
            statistics: data.statistics,
            zoom: data.layout.default_zoom,
            centerX: data.layout.default_center_x,
            centerY: data.layout.default_center_y,
            loading: false
          });
          return 'loaded';
        } catch (error: any) {
          if (error.response?.status === 404) {
            set({
              layout: null,
              positions: [],
              zones: [],
              unpositionedWorkshops: [],
              statistics: null,
              loading: false,
              error: null
            });
            return 'no_layout';
          }
          set({ 
            error: error.response?.data?.message || 'Failed to fetch plant mapping dashboard by plant',
            loading: false 
          });
          return 'error';
        }
      },

      clearMappingData: () => set({
        layout: null,
        positions: [],
        zones: [],
        unpositionedWorkshops: [],
        statistics: null,
        loading: false,
        error: null,
      }),

      setLayoutBackgroundOpacity: (opacity: number) => {
        const { layout } = get();
        if (!layout) return;
        set({
          layout: {
            ...layout,
            background_opacity: Math.max(0, Math.min(1, opacity))
          }
        });
      },

      // Position Actions
      updatePosition: async (id: number, x: number, y: number, zone?: string) => {
        set({ loading: true, error: null });
        try {
          // Check if this is moving to unpositioned queue (top area)
          // We use 0.05 coordinates for unpositioned, so check for that specifically
          const isUnpositioned = (x <= 0.1 && y <= 0.1) || (x === 0.05 && y === 0.05);
          const updateData: any = { 
            x, 
            y, 
            is_positioned: !isUnpositioned,
            zone: isUnpositioned ? '' : (zone || '')
          };
          
          console.log('Updating position:', { id, x, y, zone, isUnpositioned, updateData });
          
          const response = await workshopPositionApi.update(id, updateData);
          
          console.log('Update response:', response.data);
          const { positions, unpositionedWorkshops, statistics } = get();
          
          // Ensure the response has the expected structure
          if (!response.data || !response.data.workshop) {
            console.error('Invalid response structure:', response.data);
            throw new Error('Invalid response from server');
          }
          
          const updatedPosition = response.data;
          const workshop = updatedPosition.workshop;
          
          // Update positions array
          const updatedPositions = positions.map(pos => 
            pos.id === id ? updatedPosition : pos
          );
          
          // Update unpositioned workshops array and statistics in real-time
          let updatedUnpositionedWorkshops = [...unpositionedWorkshops];
          let updatedStatistics = statistics ? { ...statistics } : null;
          
          if (updatedPosition.is_positioned) {
            // Workshop moved from unpositioned to positioned
            // Remove from unpositioned list
            updatedUnpositionedWorkshops = unpositionedWorkshops.filter(w => w.id !== workshop.id);
          } else {
            // Workshop moved from positioned to unpositioned
            // Add to unpositioned list if not already there
            const workshopExists = unpositionedWorkshops.some(w => w.id === workshop.id);
            if (!workshopExists) {
              updatedUnpositionedWorkshops.push({
                id: workshop.id,
                workshop_number: workshop.workshop_number,
                title: workshop.title,
                status: workshop.status,
                cc_category: workshop.cc_category || '',
                sqdcm_category: workshop.sqdcm_category,
                category: workshop.category || '',
                plant: workshop.plant,
                department: workshop.department,
                department_name: workshop.department_name || '',
                estimated_savings_per_year: workshop.estimated_savings_per_year || 0,
                estimated_savings_unit: workshop.estimated_savings_unit || '',
                startup_date: workshop.startup_date || null,
                deadline: workshop.deadline || null,
                alv_id: workshop.alv_id || null
              });
            }
          }
          
          // Recalculate statistics based on actual current state
          if (updatedStatistics) {
            const positionedCount = updatedPositions.filter(pos => pos.is_positioned).length;
            const unpositionedCount = updatedUnpositionedWorkshops.length;
            
            updatedStatistics.positioned_workshops = positionedCount;
            updatedStatistics.unpositioned_workshops = unpositionedCount;
            // updatedStatistics.positioning_progress = 
              updatedStatistics.total_workshops > 0 
                ? (positionedCount / updatedStatistics.total_workshops) * 100 
                : 0;
          }
          
          set({ 
            positions: updatedPositions,
            unpositionedWorkshops: updatedUnpositionedWorkshops,
            statistics: updatedStatistics,
            loading: false 
          });
          
          console.log('Real-time update completed:', {
            positioned: updatedStatistics?.positioned_workshops,
            unpositioned: updatedStatistics?.unpositioned_workshops,
            progress: updatedStatistics ? 
              (updatedStatistics.total_workshops > 0 
                ? (updatedStatistics.positioned_workshops / updatedStatistics.total_workshops) * 100 
                : 0) : 0,
            totalWorkshops: updatedStatistics?.total_workshops,
            actualPositionedCount: updatedPositions.filter(pos => pos.is_positioned).length,
            actualUnpositionedCount: updatedUnpositionedWorkshops.length
          });
          
        } catch (error: any) {
          console.error('Update position error:', error);
          set({ 
            error: error.response?.data?.message || 'Failed to update position',
            loading: false 
          });
          
          // Only refresh on critical errors, not for every failed update
          if (error.response?.status >= 500) {
            const { layout } = get();
            if (layout) {
              get().fetchDashboard(layout.id);
            }
          }
        }
      },

      bulkUpdatePositions: async (positionsData: DragPosition[]) => {
        set({ loading: true, error: null });
        try {
          const response = await workshopPositionApi.bulkUpdate({
            positions: positionsData,
            change_reason: 'user_drag'
          });
          
          // Update positions in real-time
          const { positions, unpositionedWorkshops, statistics } = get();
          let updatedPositions = [...positions];
          let updatedUnpositionedWorkshops = [...unpositionedWorkshops];
          let updatedStatistics = statistics ? { ...statistics } : null;
          
          // Process each updated position
          if (response.data && response.data.positions) {
            response.data.positions.forEach((updatedPos: any) => {
              const workshop = updatedPos.workshop;
              
              // Update positions array
              const existingIndex = updatedPositions.findIndex(pos => pos.id === updatedPos.id);
              if (existingIndex >= 0) {
                updatedPositions[existingIndex] = updatedPos;
              }
              
              // Handle unpositioned workshops changes
              if (updatedPos.is_positioned) {
                // Remove from unpositioned list if it was there
                updatedUnpositionedWorkshops = updatedUnpositionedWorkshops.filter(w => w.id !== workshop.id);
              } else {
                // Add to unpositioned list if not already there
                const workshopExists = updatedUnpositionedWorkshops.some(w => w.id === workshop.id);
                if (!workshopExists) {
                  updatedUnpositionedWorkshops.push({
                    id: workshop.id,
                    workshop_number: workshop.workshop_number,
                    title: workshop.title,
                    status: workshop.status,
                    cc_category: workshop.cc_category || '',
                    sqdcm_category: workshop.sqdcm_category,
                    category: workshop.category || '',
                    plant: workshop.plant,
                    department: workshop.department,
                    department_name: workshop.department_name || '',
                    estimated_savings_per_year: workshop.estimated_savings_per_year || 0,
                    estimated_savings_unit: workshop.estimated_savings_unit || '',
                    startup_date: workshop.startup_date || null,
                    deadline: workshop.deadline || null,
                    alv_id: workshop.alv_id || null
                  });
                }
              }
            });
            
            // Recalculate statistics
            if (updatedStatistics) {
              const positionedCount = updatedPositions.filter(pos => pos.is_positioned).length;
              const unpositionedCount = updatedUnpositionedWorkshops.length;
              
              updatedStatistics.positioned_workshops = positionedCount;
              updatedStatistics.unpositioned_workshops = unpositionedCount;
              // updatedStatistics.positioning_progress = 
                updatedStatistics.total_workshops > 0 
                  ? (positionedCount / updatedStatistics.total_workshops) * 100 
                  : 0;
            }
          }
          
          set({ 
            positions: updatedPositions,
            unpositionedWorkshops: updatedUnpositionedWorkshops,
            statistics: updatedStatistics,
            loading: false 
          });
          
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to update positions',
            loading: false 
          });
          
          // Only refresh on critical errors
          if (error.response?.status >= 500) {
            const { layout } = get();
            if (layout) {
              get().fetchDashboard(layout.id);
            }
          }
        }
      },

      autoPositionWorkshops: async (strategy: string, options: any = {}) => {
        const { layout } = get();
        if (!layout) return;
        
        set({ loading: true, error: null });
        try {
          await workshopPositionApi.autoPosition(layout.id, {
            strategy: strategy as any,
            ...options
          });
          
          // Refresh dashboard
          await get().fetchDashboard(layout.id);
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to auto-position workshops',
            loading: false 
          });
        }
      },

      createMissingPositions: async () => {
        const { layout } = get();
        if (!layout) return;
        
        set({ loading: true, error: null });
        try {
          const response = await workshopPositionApi.createMissingPositions(layout.id);
          
          // Handle real-time updates if the response includes new positions
          if (response.data && response.data.positions) {
            const { positions } = get();
            const newPositions = [...positions];
            
            // Add any new positions that were created
            response.data.positions.forEach((newPos: any) => {
              const existingIndex = newPositions.findIndex(pos => pos.id === newPos.id);
              if (existingIndex === -1) {
                newPositions.push(newPos);
              }
            });
            
            set({ positions: newPositions, loading: false });
          } else {
            // Fallback: refresh dashboard
            await get().fetchDashboard(layout.id);
          }
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to create missing positions',
            loading: false 
          });
        }
      },

      // Zone Actions
      createZone: async (zoneData: Partial<PlantZone>) => {
        const { layout } = get();
        if (!layout) return;
        
        set({ loading: true, error: null });
        try {
          const response = await plantZoneApi.create({
            plant_layout: layout.id,
            ...zoneData
          } as any);
          
          const { zones } = get();
          set({ 
            zones: [...zones, response.data],
            loading: false 
          });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to create zone',
            loading: false 
          });
        }
      },

      updateZone: async (id: number, data: Partial<PlantZone>) => {
        set({ loading: true, error: null });
        try {
          const response = await plantZoneApi.update(id, data);
          const { zones } = get();
          
          const updatedZones = zones.map(zone => 
            zone.id === id ? response.data : zone
          );
          
          set({ zones: updatedZones, loading: false });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to update zone',
            loading: false 
          });
        }
      },

      deleteZone: async (id: number) => {
        set({ loading: true, error: null });
        try {
          await plantZoneApi.delete(id);
          const { zones } = get();
          
          const updatedZones = zones.filter(zone => zone.id !== id);
          
          set({ zones: updatedZones, loading: false });
        } catch (error: any) {
          set({ 
            error: error.response?.data?.message || 'Failed to delete zone',
            loading: false 
          });
        }
      },

      // UI Actions
      setSelectedWorkshop: (workshop: WorkshopPosition | null) => 
        set({ selectedWorkshop: workshop }),
      
      setSelectedZone: (zone: PlantZone | null) => 
        set({ selectedZone: zone }),
      
      setDragging: (isDragging: boolean, workshop?: WorkshopPosition | null) => 
        set({ isDragging, draggedWorkshop: workshop || null }),
      
      setZoom: (zoom: number) => 
        set({ zoom: Math.max(0.1, Math.min(zoom, 5)) }),
      
      setCenter: (x: number, y: number) => 
        set({ centerX: x, centerY: y }),
      
      setShowZones: (show: boolean) => 
        set({ showZones: show }),
      
      setShowUnpositioned: (show: boolean) => 
        set({ showUnpositioned: show }),
      
      setFilters: (filters) => {
        const { filterStatus, filterCategory, filterDepartment, filterDeadlineFrom, filterDeadlineTo, filterAlvId } = get();
        
        let dateChanged = false;
        let newStartDate = filters.deadlineFrom !== undefined ? filters.deadlineFrom : filterDeadlineFrom;
        let newEndDate = filters.deadlineTo !== undefined ? filters.deadlineTo : filterDeadlineTo;

        // Update local storage if deadlines are changed
        if (filters.deadlineFrom !== undefined) {
          dateChanged = true;
          if (filters.deadlineFrom) {
            localStorage.setItem(STORAGE_KEY_FROM, filters.deadlineFrom);
          } else {
            localStorage.removeItem(STORAGE_KEY_FROM);
          }
        }
        
        if (filters.deadlineTo !== undefined) {
          dateChanged = true;
          if (filters.deadlineTo) {
            localStorage.setItem(STORAGE_KEY_TO, filters.deadlineTo);
          } else {
            localStorage.removeItem(STORAGE_KEY_TO);
          }
        }

        // Dispatch event if dates changed
        if (dateChanged) {
          const event = new CustomEvent(EVENT_NAME, { 
            detail: { startDate: newStartDate, endDate: newEndDate } 
          });
          window.dispatchEvent(event);
        }

        set({ 
          filterStatus: filters.status !== undefined ? filters.status : filterStatus,
          filterCategory: filters.category !== undefined ? filters.category : filterCategory,
          filterDepartment: filters.department !== undefined ? filters.department : filterDepartment,
          filterDeadlineFrom: newStartDate,
          filterDeadlineTo: newEndDate,
          filterAlvId: filters.alvId !== undefined ? filters.alvId : filterAlvId
        });
      },

      // Utility Actions
      getFilteredPositions: () => {
        const { positions, filterStatus, filterCategory, filterDepartment, filterDeadlineFrom, filterDeadlineTo, filterAlvId } = get();
        
        console.log('Filtering with:', { filterStatus, filterCategory, filterDepartment, filterDeadlineFrom, filterDeadlineTo, filterAlvId });
        console.log('Total positions:', positions.length);
        
        const filtered = positions.filter(pos => {
          // Check status filter
          if (filterStatus && pos.workshop.status !== filterStatus) {
            console.log(`Workshop ${pos.workshop.workshop_number} filtered out by status: ${pos.workshop.status} !== ${filterStatus}`);
            return false;
          }
          
          // Check category filter
          if (filterCategory) {
            // Handle case where workshop category might be undefined/null
            const workshopCategory = pos.workshop.category || '';
            console.log(`Checking workshop ${pos.workshop.workshop_number}: category="${workshopCategory}" vs filter="${filterCategory}"`);
            
            let categoryMatches = false;
            
            if (filterCategory === 'EMPTY') {
              // Special case: filter for workshops with no category
              categoryMatches = workshopCategory === '';
            } else {
              // Check if workshop category matches the filter
              // Workshop category might be stored as full description (e.g., "213100 - Waste and scrap")
              // or just the code (e.g., "213100"), so we need to check both ways:
              // 1. Exact match: workshopCategory === filterCategory
              // 2. Workshop has full description, filter is just code: workshopCategory.startsWith(filterCategory + ' - ')
              // 3. Filter has full description, workshop is just code: filterCategory.startsWith(workshopCategory + ' - ')
              categoryMatches = workshopCategory === filterCategory || 
                              workshopCategory.startsWith(filterCategory + ' - ') ||
                              (workshopCategory ? filterCategory.startsWith(workshopCategory + ' - ') : false);
            }
            
            if (!categoryMatches) {
              console.log(`Workshop ${pos.workshop.workshop_number} filtered out by category: "${workshopCategory}" doesn't match "${filterCategory}"`);
              return false;
            }
          }
          
          // Check department filter
          if (filterDepartment && pos.workshop.department !== filterDepartment) {
            console.log(`Workshop ${pos.workshop.workshop_number} filtered out by department: ${pos.workshop.department} !== ${filterDepartment}`);
            return false;
          }

          // Check deadline filters
          if (pos.workshop.deadline) {
            if (filterDeadlineFrom && pos.workshop.deadline < filterDeadlineFrom) {
              return false;
            }
            if (filterDeadlineTo && pos.workshop.deadline > filterDeadlineTo) {
              return false;
            }
          } else if (filterDeadlineFrom || filterDeadlineTo) {
            // If workshop has no deadline but filters are applied, filter it out
            return false;
          }

          // Check ALV ID filter
          if (filterAlvId) {
            const workshopAlvId = pos.workshop.alv_id || '';
            if (workshopAlvId !== filterAlvId) {
              return false;
            }
          }
          
          return true;
        });
        
        console.log('Filtered positions:', filtered.length);
        return filtered;
      },

      getFilteredUnpositionedWorkshops: () => {
        const { unpositionedWorkshops, filterStatus, filterCategory, filterDepartment, filterDeadlineFrom, filterDeadlineTo, filterAlvId } = get();
        
        return unpositionedWorkshops.filter(workshop => {
          // Check status filter
          if (filterStatus && workshop.status !== filterStatus) return false;
          
          // Check category filter
          if (filterCategory) {
            const workshopCategory = workshop.category || '';
            let categoryMatches = false;
            
            if (filterCategory === 'EMPTY') {
              categoryMatches = workshopCategory === '';
            } else {
              categoryMatches = workshopCategory === filterCategory || 
                              workshopCategory.startsWith(filterCategory + ' - ') ||
                              (workshopCategory ? filterCategory.startsWith(workshopCategory + ' - ') : false);
            }
            
            if (!categoryMatches) return false;
          }
          
          // Check department filter
          if (filterDepartment && workshop.department !== filterDepartment) return false;

          // Check deadline filters
          if (workshop.deadline) {
            if (filterDeadlineFrom && workshop.deadline < filterDeadlineFrom) return false;
            if (filterDeadlineTo && workshop.deadline > filterDeadlineTo) return false;
          } else if (filterDeadlineFrom || filterDeadlineTo) {
            return false;
          }

          // Check ALV ID filter
          if (filterAlvId) {
            const workshopAlvId = workshop.alv_id || '';
            if (workshopAlvId !== filterAlvId) return false;
          }
          
          return true;
        });
      },

      getWorkshopAtPosition: (x: number, y: number, threshold: number = 0.02) => {
        const { positions } = get();
        
        return positions.find(pos => {
          if (!pos.is_positioned) return false;
          
          const distance = plantMappingUtils.calculateDistance(x, y, pos.x, pos.y);
          return distance <= threshold;
        }) || null;
      },

      getZoneAtPosition: (x: number, y: number) => {
        const { zones } = get();
        
        return zones.find(zone => 
          plantMappingUtils.isPointInZone(x, y, zone)
        ) || null;
      },

      clearError: () => set({ error: null }),
      
      reset: () => set({
        layout: null,
        positions: [],
        zones: [],
        unpositionedWorkshops: [],
        statistics: null,
        loading: false,
        error: null,
        selectedWorkshop: null,
        selectedZone: null,
        isDragging: false,
        draggedWorkshop: null,
        zoom: 1,
        centerX: 0.5,
        centerY: 0.5,
        showZones: true,
        showUnpositioned: true,
        filterStatus: '',
        filterCategory: '',
        filterDepartment: null,
        filterDeadlineFrom: localStorage.getItem(STORAGE_KEY_FROM) || null,
        filterDeadlineTo: localStorage.getItem(STORAGE_KEY_TO) || null,
        filterAlvId: null,
      }),
    };
  },
  {
    name: 'plant-mapping-store',
  }
 )
);
